import { Command, CommandResult, Message } from '@/store/entities/Message';
import { Events, EventSystem, UNIVERSAL_TYPE } from "./EventSystem"
import { v4 as uuid } from 'uuid';
import { SocketClient } from '@/modules/SocketClient';

export type CustomEvents = {
  [K in Command]: (msg: Message<K>) => void
} & {
  connected: (msg: {
    id: string,
    resumeToken?: string,
    bindingEpoch?: number
  }) => void
  reconnect: () => void
  close: () => void
  forceClose: () => void
  ping: () => void
  pong: () => void
  'command-result': (result: CommandResult) => void
}

enum MessageStatus {
  COMPLETE = 0,
  PENDING = 1,
  WAITING = 2
}

interface MessageRecord {
  messageId: string,
  status: MessageStatus
  event: string,
  intentId?: string
  eventType: number
  retry: number
  timestamp: number
  createDate: number,
  remove?: boolean
  messageHandle: (socket: WebSocket) => void
}

const UPGRADE_MESSAGE = 6
const DURABLE_MUTATION_EVENTS = new Set(['action', 'continue', 'restart'])

function isDurableMutation(event: string) {
  return DURABLE_MUTATION_EVENTS.has(event)
}

class WebSocketClient<E extends Events> extends EventSystem<E> {
  private url: string
  id: string
  stack: MessageRecord[] = []
  hooksStack: MessageRecord[] = []
  master: SocketClient<E>
  private socket!: WebSocket

  stackTimer: any
  reconnectInterval: any
  heartBeatTimer: any
  staleConnectionTimer: any
  awaiting = false
  private receivedMessageIds: Set<string> = new Set()
  private receivedMessageOrder: string[] = []

  private readonly heartbeatTimeout = 15000
  private readonly heartbeatInterval = 10000
  private readonly foregroundHeartbeatGrace = 3000
  private foregroundHeartbeatGraceUntil = 0

  private handleVisibilityChange = () => {
    if (document.hidden) {
      clearTimeout(this.heartBeatTimer)
      clearTimeout(this.staleConnectionTimer)
      return
    }

    // Visibility is not evidence of connectivity. If the last real response
    // is already stale, replace the transport immediately; otherwise probe it.
    if (
      Date.now() - this.lastPing > this.heartbeatTimeout &&
      this.socket?.readyState === WebSocket.OPEN
    ) {
      this.socket.close()
      return
    }
    this.foregroundHeartbeatGraceUntil = Date.now() + this.foregroundHeartbeatGrace
    this.heartBeat()
    this.scheduleHeartbeat()
    this.scheduleStaleConnectionCheck()
  }

  leave = true
  lastPing: number = Date.now()

  constructor(url: string, id: string, master: SocketClient<E>) {
    super()

    this.master = master

    this.url = url
    this.id = id

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  private scheduleStack(delay = 0) {
    if (this.stackTimer !== undefined && this.stackTimer !== null) return
    this.stackTimer = setTimeout(() => {
      this.stackTimer = null
      this.processStack()
    }, delay)
  }

  private wakeStack() {
    clearTimeout(this.stackTimer)
    this.stackTimer = null
    this.scheduleStack()
  }

  private processStack() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || this.awaiting) return

    const messageRecord = this.stack[0]
    if (!messageRecord) return

    if (this.stack.length > 50) {
      this.socket.close()
      return
    }

    if (messageRecord.status === MessageStatus.COMPLETE) {
      this.stack.splice(0, 1)
      this.wakeStack()
      return
    }

    if (messageRecord.status === MessageStatus.WAITING) {
      messageRecord.messageHandle(this.socket)
      messageRecord.timestamp = Date.now()
      messageRecord.status = MessageStatus.PENDING
    }

    const retryIn = 1500 - (Date.now() - messageRecord.timestamp)
    if (retryIn > 0) {
      this.scheduleStack(retryIn)
      return
    }

    messageRecord.retry++
    console.log('retry', messageRecord.retry, messageRecord.eventType, messageRecord.messageId)
    if (messageRecord.retry > 10) {
      this.socket.close()
      return
    }
    messageRecord.status = MessageStatus.WAITING
    this.scheduleStack()
  }

  private scheduleHeartbeat() {
    clearTimeout(this.heartBeatTimer)
    if (document.hidden) return
    this.heartBeatTimer = setTimeout(() => {
      this.heartBeatTimer = null
      this.heartBeat()
      this.scheduleHeartbeat()
    }, this.heartbeatInterval)
  }

  private scheduleStaleConnectionCheck() {
    clearTimeout(this.staleConnectionTimer)
    if (document.hidden || this.socket?.readyState !== WebSocket.OPEN) return

    const deadline = Math.max(
      this.lastPing + this.heartbeatTimeout,
      this.foregroundHeartbeatGraceUntil
    )
    this.staleConnectionTimer = setTimeout(() => {
      this.staleConnectionTimer = null
      if (document.hidden || this.socket?.readyState !== WebSocket.OPEN) return
      if (
        Date.now() >= this.foregroundHeartbeatGraceUntil &&
        Date.now() - this.lastPing >= this.heartbeatTimeout
      ) this.socket.close()
      else this.scheduleStaleConnectionCheck()
    }, Math.max(1, deadline - Date.now()))
  }

  registerPing() {
    this.lastPing = Date.now()
    this.foregroundHeartbeatGraceUntil = 0
    this.scheduleStaleConnectionCheck()
  }
  
  getSocket() {
    return this.socket
  }
  
  init() {
    this.awaiting = true
    this.socket = new WebSocket(`${this.url}`)
    this.initSocket(0)
  }

  registerMessageComplete(messageId: string) {
    const stackMessage = this.stack.find(m => m.messageId === messageId)
    if (stackMessage) {
      stackMessage.status = MessageStatus.COMPLETE
      if (stackMessage.eventType === UPGRADE_MESSAGE) {
        this.master.registerUpgradeComplete(this.id)
      }
      this.wakeStack()
    }
  }

  addToStack(msg: MessageRecord) {
    if (msg.eventType === UPGRADE_MESSAGE) {
      // A reconnected transport must establish its current room/game binding
      // before replaying commands whose closures still contain the old socket
      // id. Keep only the newest upgrade and put it ahead of restored work.
      this.stack = this.stack.filter(message => message.eventType !== UPGRADE_MESSAGE)
      this.stack.unshift(msg)
      this.wakeStack()
      return
    }
    if (isDurableMutation(msg.event)) {
      if (msg.intentId) {
        // A recovery retry reuses the same semantic actionId. Replace its old
        // transport closure (which may still contain the previous socket id)
        // instead of replaying both copies after reconnect.
        this.stack = this.stack.filter(message =>
          !isDurableMutation(message.event) || message.intentId !== msg.intentId
        )
      }
      // Mutations are ordered after the current binding upgrade but before
      // replaceable queries. A query waiting for its ACK must never block a
      // player's action/continue/restart intent.
      const insertAt = this.stack.findIndex(message =>
        message.eventType !== UPGRADE_MESSAGE &&
        !isDurableMutation(message.event)
      )
      this.stack.splice(insertAt === -1 ? this.stack.length : insertAt, 0, msg)
      this.wakeStack()
      return
    }
    if (msg.eventType === UNIVERSAL_TYPE) {
      this.stack.push(msg)
      this.scheduleStack()
      return
    }
    this.stack.forEach(message => {
      if (message.status === MessageStatus.WAITING && message.eventType === msg.eventType) {
        message.remove = true
      }
    })
    this.stack = this.stack.filter(m => !m.remove)
    this.stack.push(msg)
    this.wakeStack()
  }

  reconnect(err: CloseEvent) {
    console.error('socket closed')

    if (err.code === 1006 || err.code === 1005) {
      console.warn('disconnect from client')
      this.reconnectInterval = setTimeout(() => {
        if (this.socket.readyState === WebSocket.CLOSED) {
          this.master.scheduleReconnect()
        }
      }, 1000)
    }
    else {
      console.warn('disconnect from server')
      this.reconnectInterval = setTimeout(() => {
        if (this.socket.readyState === WebSocket.CLOSED) {
          this.master.scheduleReconnect()
        }
      }, 3000)
    }
  }

  closeHandler(err: CloseEvent) {
    this.reconnect(err)
  }

  initSocket(count: number) {
    if (count >= 10) window.location.reload()
    
    this.stack.forEach(msg => {
      if (msg.status === MessageStatus.PENDING) {
        msg.status = MessageStatus.WAITING
      }
    })

    this.socket.onerror = () => {
      this.socket.close()
    }

    this.socket.onclose = this.closeHandler.bind(this)

    this.socket.onopen = () => {
      this.master.clearReconnectInterval()
      count = 0
      this.awaiting = true
      this.lastPing = Date.now()
      this.scheduleHeartbeat()
      this.scheduleStaleConnectionCheck()

      this.socket.onmessage = msg => {
        const msgData = JSON.parse(msg.data.toString())
        const { event, data, messageId } = msgData
        this.registerPing()

        if (event === 'complete') {
          this.registerMessageComplete(messageId)
        }

        if (event !== 'heartbeat' && event !== 'complete') {
          this.socket.send(JSON.stringify({
            event: 'complete',
            messageId: messageId,
            data: {}
          }))

          if (this.receivedMessageIds.has(messageId)) return
          this.receivedMessageIds.add(messageId)
          this.receivedMessageOrder.push(messageId)
          if (this.receivedMessageOrder.length > 500) {
            const expiredId = this.receivedMessageOrder.shift()
            if (expiredId) this.receivedMessageIds.delete(expiredId)
          }

          this.trigger(event, data)
          if (event === 'connected' || event === 'reconnect') {
            // connected handlers synchronously queue the binding UPGRADE. Only
            // after they return may restored gameplay mutations start moving.
            this.awaiting = false
            this.wakeStack()
          }
        }
      }

    }
  }

  heartBeat() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        event: 'heartbeat',
        messageId: uuid(),
        data: {}
      }))
    }
  }

  clear() {
    if (this.socket) {
      this.socket.onclose = null
      this.socket.onmessage = null
      this.socket.onerror = null
      this.socket.onopen = null
    }
    clearTimeout(this.stackTimer)
    clearTimeout(this.heartBeatTimer)
    clearTimeout(this.staleConnectionTimer)
    clearTimeout(this.reconnectInterval)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.kill()
    this.clearEvents()
  }

  clearEvents() {
    this.events.clear()
  }

  pendingMessages() {
    // Queries are snapshots/probes and are regenerated by the connected hook.
    // Replaying them ahead of a gameplay mutation creates head-of-line stalls.
    return this.stack
      .filter(message =>
        message.status !== MessageStatus.COMPLETE &&
        isDurableMutation(message.event)
      )
      .map(message => ({
        ...message,
        status: MessageStatus.WAITING,
        timestamp: Date.now(),
        retry: 0
      }))
  }

  restorePendingMessages(messages: MessageRecord[]) {
    const durableMessages = messages.filter(message => isDurableMutation(message.event))
    this.awaiting = durableMessages.length > 0
    durableMessages.forEach(message => this.addToStack(message))
  }

  cancelPendingIntent(intentId: string) {
    this.stack = this.stack.filter(message => message.intentId !== intentId)
    this.wakeStack()
  }

  kill() {
    if (!this.socket) return
    try {
      if (this.socket.readyState === WebSocket.OPEN) {
        this.socket.close()
      }
      else {
        setTimeout(() => {
          this.kill()
        }, 1000)
      }
    }
    catch (e) {}
  }

  off<K extends keyof E>(keys: K[]) {
    keys.forEach(key => {
      this.events.delete(key)
    })
  }

  emitter<K extends keyof E>(key: K, data: Parameters<E[K]>[], type = UNIVERSAL_TYPE): void {
    const id = uuid()
    const intentId = (data as unknown as { meta?: { actionId?: unknown } })?.meta?.actionId

    if (key === 'heartbeat') return
    if (key === 'complete') return
    if (key === 'close') return
    if (key === 'error') return
    if (key === 'forceClose') return

    const msg = {
      messageId: id,
      event: String(key),
      intentId: typeof intentId === 'string' ? intentId : undefined,
      eventType: type,
      status: MessageStatus.WAITING,
      timestamp: Date.now(),
      createDate: Date.now(),
      retry: 0,
      messageHandle: (socket: WebSocket) => {
        socket.send(JSON.stringify({
          event: key,
          messageId: id,
          data
        }))
      }
    }

    this.addToStack(msg)
  }
}

export { WebSocketClient, isDurableMutation }
